import * as React from "react";
import { IWalletDetails, sellAbilities } from "@market/interfaces/IWallet";
import { Button, Space, Typography, Tooltip } from "antd";
import CurrencyElement from "@appComponents/CurrencyElement/CurrencyElement";
import { MAIN_CURRENCY } from "@bundles/consts";
import { Link } from "react-router-dom";
import CurrencyAvatar from "./parts/CurrencyAvatar";
import { Location } from "history";
import Big from "big.js";
import { classNames } from "@features/utils/reactFunctions";
import {
  CURRENCIES_AVAILABLE_TO_BUY,
  CURRENCIES_AVAILABLE_TO_STAKE,
  CURRENCIES_AVAILABLE_TO_SELL,
  CURRENCIES_AVAILABLE_TO_DEPOSIT,
} from "@funds/consts";
import {
  FUNDS_BUY_FORM,
  FUNDS_DEPOSIT_FORM,
  FUNDS_SELL_FORM,
  FUNDS_STAKING_FORM,
  FUNDS_WITHDRAW_FORM,
} from "@funds/config";
import {
  isTier1AndAbove,
  isTier1UkRestricted,
} from "@funds/features/helpers/functions";
import ConditionalWrapper from "@appComponents/ConditionalWrapper";
import {
  productRestrictedForUsMessage,
  tier1RequiredMessage,
  tier1UkRestrictedMessage,
} from "../..";
import {
  tradeAnyRestrictionMessage,
  tradeStakeRestrictionMessage,
  tradeUserStatementRestrictionMessage,
} from "@funds/features/helpers/validationMessages";
import { restrictedByUserStatementKey } from "@funds/features/helpers/validation";
import { AGREEMENT_TYPES } from "@services/UserAgreements.service";
import { TUserAgreements } from "@features/reducer.info";

import * as fundsStyle from "../../../../FundsAppStyle.module.scss";
import * as styles from "./FundsTabStyles.module.scss";

export const stakeRestrictionMessage =
  "This feature is currently restricted on your account";

// avatarBgColor order is important, its reflected in the chart
export const avatarBgColor: { [key: string]: string } = {
  eth: "#1C1CF5",
  btc: "#F29B37",
  staked_eth: "#1C80F5",
  bft: "#81109C",
  bfusd: "#50af95",
  default: "#FF6161",
};

const big = Big();

export const fundsColumns = ({
  location,
  tier,
  usResident,
  agreements,
  isUk,
  isUkRiskModalEnabled,
  setRiskModalProps,
  isUserCanStake,
}: {
  location: Location;
  tier: string;
  usResident: boolean;
  isUk: boolean;
  isUkRiskModalEnabled: boolean;
  agreements: TUserAgreements;
  setRiskModalProps: (props: object) => void;
  isUserCanStake?: boolean;
}) => [
  {
    title: "NAME",
    dataIndex: "currency_name",
    key: "currency_name",
    ellipsis: true,
    width: "30%",
    sorter: (a: IWalletDetails, b: IWalletDetails) =>
      a.currency_name.localeCompare(b.currency_name),
    render: (text: string, record: IWalletDetails, index: number) => {
      return (
        <div className={styles.currencyName}>
          <CurrencyAvatar
            currency={record.currency}
            background={
              avatarBgColor[record.currency] ?? avatarBgColor["default"]
            }
          />
          &nbsp;
          <span>{text}</span>
        </div>
      );
    },
  },
  {
    title: "BALANCE",
    dataIndex: "balance",
    key: "balance",
    width: "23%",
    sorter: (a: IWalletDetails, b: IWalletDetails) =>
      big(a.total_usd ?? 0).gt(b.total_usd ?? 0) ? 0 : 1,
    render: (text: string, record: IWalletDetails) => {
      const value =
        record.currency === MAIN_CURRENCY ? record.balance : record.total_usd;

      return (
        <>
          <Typography.Text>
            <CurrencyElement
              value={record.balance}
              currency={record.currency}
            />
          </Typography.Text>

          <br />
          <Typography.Text className={styles.valueInUsd}>
            &asymp;&nbsp;
            <CurrencyElement value={Number(value)} currency={"usd"} />
          </Typography.Text>
        </>
      );
    },
  },
  {
    title: "ACTIONS",
    dataIndex: "country",
    key: "country",
    align: "right" as const,
    width: "47%",
    render: (text: string, record: IWalletDetails, index: number) => {
      const basePath = `${location.pathname.split("/funds")[0]}/funds`;

      const isCanBuyRestrictedByUserStatement =
        record.abilities.can_buy !== true &&
        record.abilities.can_buy.length === 1 &&
        record.abilities.can_buy[0] === restrictedByUserStatementKey;

      const isCanSellRestrictedByUserStatement =
        record.abilities.can_sell !== true &&
        record.abilities.can_sell.length === 1 &&
        record.abilities.can_sell[0] === restrictedByUserStatementKey;

      return (
        <Space wrap className={styles.actions}>
          {CURRENCIES_AVAILABLE_TO_STAKE.includes(record.currency) && (
            <ConditionalWrapper
              condition={isTier1AndAbove(tier) || usResident || !isUserCanStake}
              wrapper={(childern) => (
                <Tooltip
                  title={
                    !isUserCanStake
                      ? stakeRestrictionMessage
                      : usResident
                      ? productRestrictedForUsMessage
                      : tradeStakeRestrictionMessage
                  }
                >
                  {childern}
                </Tooltip>
              )}
            >
              <Link
                data-cy={`${record.currency}-stake-btn`}
                to={`${basePath}/${FUNDS_STAKING_FORM}`}
                className={classNames(
                  fundsStyle.button,
                  !isUk ? fundsStyle.buttonPurple : undefined
                )}
                onClick={(e) => {
                  if (isUkRiskModalEnabled && !agreements.eth_staking) {
                    e.preventDefault();
                    setRiskModalProps({
                      agreementType: AGREEMENT_TYPES.ETH_STAKING,
                      redirectionPath: `${basePath}/${FUNDS_STAKING_FORM}`,
                    });
                  }
                }}
              >
                <Button
                  disabled={
                    isTier1AndAbove(tier) || usResident || !isUserCanStake
                  }
                >
                  Stake
                </Button>
              </Link>
            </ConditionalWrapper>
          )}

          {CURRENCIES_AVAILABLE_TO_BUY.includes(record.currency) && (
            <>
              <ConditionalWrapper
                condition={
                  isTier1AndAbove(tier) || record.abilities.can_buy !== true
                }
                wrapper={(childern) => (
                  <Tooltip
                    title={
                      isTier1AndAbove(tier)
                        ? tradeAnyRestrictionMessage
                        : isCanBuyRestrictedByUserStatement
                        ? tradeUserStatementRestrictionMessage
                        : tradeAnyRestrictionMessage
                    }
                  >
                    {childern}
                  </Tooltip>
                )}
              >
                <Link
                  data-cy={`${record.currency}-buy-btn`}
                  className={classNames(
                    fundsStyle.button,
                    !isUk ? fundsStyle.buttonGreen : undefined
                  )}
                  onClick={(e) => {
                    if (isUkRiskModalEnabled && !agreements.crypto_trading) {
                      e.preventDefault();
                      setRiskModalProps({
                        agreementType: AGREEMENT_TYPES.CRYPTO_TRADING,
                        redirectionPath: `${basePath}/${FUNDS_BUY_FORM}?currency=${record.currency}`,
                      });
                    }
                  }}
                  to={`${basePath}/${FUNDS_BUY_FORM}?currency=${record.currency}`}
                >
                  <Button
                    disabled={
                      isTier1AndAbove(tier) || record.abilities.can_buy !== true
                    }
                  >
                    Buy
                  </Button>
                </Link>
              </ConditionalWrapper>
            </>
          )}

          {CURRENCIES_AVAILABLE_TO_SELL.includes(record.currency) && (
            <ConditionalWrapper
              condition={
                isTier1AndAbove(tier) || record.abilities.can_sell !== true
              }
              wrapper={(childern) => (
                <Tooltip
                  title={
                    isTier1AndAbove(tier)
                      ? tradeAnyRestrictionMessage
                      : isCanSellRestrictedByUserStatement
                      ? tradeUserStatementRestrictionMessage
                      : tradeAnyRestrictionMessage
                  }
                >
                  {childern}
                </Tooltip>
              )}
            >
              <Link
                data-cy={`${record.currency}-sell-btn`}
                className={classNames(
                  fundsStyle.button,
                  !isUk ? fundsStyle.buttonRed : undefined
                )}
                to={`${basePath}/${FUNDS_SELL_FORM}?currency=${record.currency}`}
                onClick={(e) => {
                  if (isUkRiskModalEnabled && !agreements.crypto_trading) {
                    e.preventDefault();
                    setRiskModalProps({
                      agreementType: AGREEMENT_TYPES.CRYPTO_TRADING,
                      redirectionPath: `${basePath}/${FUNDS_SELL_FORM}?currency=${record.currency}`,
                    });
                  }
                }}
              >
                <Button
                  disabled={
                    big(record.balance).lte(0) ||
                    isTier1AndAbove(tier) ||
                    record.abilities.can_sell !== true
                  }
                >
                  Sell
                </Button>
              </Link>
            </ConditionalWrapper>
          )}
          {CURRENCIES_AVAILABLE_TO_DEPOSIT.includes(record.currency) && (
            <ConditionalWrapper
              condition={
                isTier1UkRestricted(tier) ||
                record.abilities.can_deposit !== true
              }
              wrapper={(childern) => (
                <Tooltip
                  title={
                    isTier1UkRestricted(tier)
                      ? tier1UkRestrictedMessage
                      : tradeAnyRestrictionMessage
                  }
                >
                  {childern}
                </Tooltip>
              )}
            >
              <Link
                data-cy={`${record.currency}-deposit-btn`}
                className={fundsStyle.button}
                to={`${basePath}/${FUNDS_DEPOSIT_FORM}?currency=${record.currency}`}
                onClick={(e) => {
                  if (isUkRiskModalEnabled && !agreements.crypto_deposits) {
                    e.preventDefault();
                    setRiskModalProps({
                      agreementType: AGREEMENT_TYPES.CRYPTO_DEPOSITS,
                      redirectionPath: `${basePath}/${FUNDS_DEPOSIT_FORM}?currency=${record.currency}`,
                    });
                  }
                }}
              >
                <Button
                  disabled={
                    isTier1UkRestricted(tier) ||
                    record.abilities.can_deposit !== true
                  }
                >
                  Deposit
                </Button>
              </Link>
            </ConditionalWrapper>
          )}
          {record.currency !== "staked_eth" && (
            <Link
              data-cy={`${record.currency}-withdraw-btn`}
              className={fundsStyle.button}
              to={`${basePath}/${FUNDS_WITHDRAW_FORM}?currency=${record.currency}`}
            >
              <Button>Withdraw</Button>
            </Link>
          )}
        </Space>
      );
    },
  },
];
